import type { PermissionMapping } from "@eai-toolkit/client";
import { Cancel, CheckCircle } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";

import { useGetPermissionsByResourceTypeQuery } from "../../../../store/api/permissionsApi";
import type { SelectedResource } from "../data/types";

const PermissionsList = styled(List)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: 0,
  marginTop: theme.spacing(2),
}));

const PermissionItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const PermissionText = styled(ListItemText)({
  "& .MuiListItemText-primary": {
    fontFamily: "monospace",
    fontSize: "0.9rem",
  },
});

type ActionSelectionFieldProps = {
  resource: SelectedResource | null;
  value: string | null;
  onChange: (action: string | null) => void;
};

function kebabCaseToTitleCase(kebabCaseString: string): string {
  return kebabCaseString
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getAllPermissionsFromActions(actions: PermissionMapping[]): string[] {
  const permissionSet = new Set<string>();
  for (const action of actions) {
    for (const permission of action.permissions ?? []) {
      permissionSet.add(permission);
    }
  }
  return Array.from(permissionSet).sort();
}

function ActionSelectionField({
  resource,
  value,
  onChange,
}: ActionSelectionFieldProps) {
  const { data: availableActions = [], isLoading } =
    useGetPermissionsByResourceTypeQuery(
      { resourceType: resource?.type ?? "data" },
      { skip: !resource },
    );

  const allPermissions = useMemo(
    () => getAllPermissionsFromActions(availableActions),
    [availableActions],
  );
  const selectedMapping = useMemo(
    () => availableActions.find((a) => a.action === value) ?? null,
    [availableActions, value],
  );
  const grantedPermissions = useMemo(
    () => new Set(selectedMapping?.permissions ?? []),
    [selectedMapping],
  );

  useEffect(() => {
    if (!resource || availableActions.length === 0 || isLoading) {
      return;
    }
    const isValueValid =
      value && availableActions.some((a) => a.action === value);
    if (!isValueValid) {
      const defaultAction = availableActions[0];
      if (defaultAction?.action) {
        onChange(defaultAction.action);
      }
    }
  }, [resource, value, onChange, availableActions, isLoading]);

  if (!resource) {
    return (
      <Typography color="text.secondary">
        Please select a resource first to see available actions.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading actions...</Typography>
      </Box>
    );
  }

  if (availableActions.length === 0) {
    return (
      <Typography color="text.secondary">
        No actions available for this resource type.
      </Typography>
    );
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel id="action-select-label">Action</InputLabel>
        <Select
          labelId="action-select-label"
          id="action-select"
          value={value ?? ""}
          label="Action"
          onChange={handleChange}
        >
          {availableActions.map((mapping) => (
            <MenuItem key={mapping.action} value={mapping.action}>
              {kebabCaseToTitleCase(mapping.action ?? "")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedMapping && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedMapping.description}
          </Typography>

          <PermissionsList>
            {allPermissions.map((permission) => {
              const isGranted = grantedPermissions.has(permission);
              return (
                <PermissionItem key={permission}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {isGranted ? (
                      <CheckCircle
                        color="success"
                        fontSize="small"
                        aria-label="Granted"
                      />
                    ) : (
                      <Cancel
                        color="error"
                        fontSize="small"
                        aria-label="Denied"
                      />
                    )}
                  </ListItemIcon>
                  <PermissionText primary={permission} />
                </PermissionItem>
              );
            })}
          </PermissionsList>
        </Box>
      )}
    </Box>
  );
}

export default ActionSelectionField;
